import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";

import type { ApiKey } from "../../api/schemas/integrations";
import {
	GradeFilter,
	type Task,
	TaskPriority,
	TaskSort,
	TaskStatus,
	type TaskTotals,
} from "../../api/schemas/tasks";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import {
	agentBadge,
	gradeChip,
	priorityGlyph,
	priorityLabel,
	relativeTime,
	statusChip,
	statusLabel,
} from "../../widgets/a2aUi";
import * as Icons from "../../widgets/icons";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Loaded, Model } from "./model";

type H = HtmlBuilder<Message>;

const TERMINAL: ReadonlyArray<Task["status"]> = ["done", "canceled"];

export type Row = Readonly<{
	task: Task;
	depth: number;
	childCount: number;
}>;

/**
 * Parent/child order that still honours the chosen sort: each group (a task
 * and its loaded descendants) ranks by its best-sorted member in server
 * order, at every level, so the top row's group holds the top-sorted task. A
 * task whose parent is not loaded (filtered or on a later page) is a root.
 * Descendants of a collapsed parent are skipped; `childCount` counts every
 * loaded descendant, so it matches the rows a collapse hides.
 */
export const treeRows = (
	tasks: ReadonlyArray<Task>,
	collapsed: ReadonlyArray<string>,
): ReadonlyArray<Row> => {
	const ids = new Set(tasks.map((t) => t.id));
	const children = new Map<string, Task[]>();
	for (const t of tasks)
		if (t.parent_task_id !== null && ids.has(t.parent_task_id))
			children.set(t.parent_task_id, [
				...(children.get(t.parent_task_id) ?? []),
				t,
			]);
	const index = new Map(tasks.map((t, i) => [t.id, i] as const));
	const rank = new Map<string, number>();
	const size = new Map<string, number>();
	const measure = (task: Task): void => {
		const kids = children.get(task.id) ?? [];
		kids.forEach(measure);
		rank.set(
			task.id,
			Math.min(
				index.get(task.id) ?? 0,
				...kids.map((k) => rank.get(k.id) ?? 0),
			),
		);
		size.set(
			task.id,
			kids.reduce((n, k) => n + 1 + (size.get(k.id) ?? 0), 0),
		);
	};
	const roots = tasks.filter(
		(t) => t.parent_task_id === null || !ids.has(t.parent_task_id),
	);
	roots.forEach(measure);
	const byRank = (list: ReadonlyArray<Task>): ReadonlyArray<Task> =>
		[...list].sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
	const walk = (task: Task, depth: number): ReadonlyArray<Row> => [
		{ task, depth, childCount: size.get(task.id) ?? 0 },
		...(collapsed.includes(task.id)
			? []
			: byRank(children.get(task.id) ?? []).flatMap((kid) =>
					walk(kid, depth + 1),
				)),
	];
	return byRank(roots).flatMap((t) => walk(t, 0));
};

/** Agents by key name; the signed-in user by name; other people generically (no member directory). */
const assignee = (
	h: H,
	shared: Shared,
	keys: ReadonlyArray<ApiKey>,
	id: string | null,
): Html => {
	if (id === null) return h.empty;
	const key = keys.find((k) => k.id === id);
	if (key !== undefined) return agentBadge(h, key.description);
	return h.span(
		[h.Class("text-sm")],
		[
			id === shared.user.id
				? shared.user.name
				: i18next.t("tasks.list.someoneElse"),
		],
	);
};

const dueText = (due: string): string => {
	const ms = Date.parse(due.length === 10 ? `${due}T00:00:00Z` : due);
	return Number.isNaN(ms)
		? due
		: new Intl.DateTimeFormat(i18next.language, {
				timeZone: "UTC",
				month: "short",
				day: "numeric",
			}).format(ms);
};

const meta = (h: H, task: Task, now: number, assigned: Html): Html => {
	const overdue =
		task.due !== null &&
		!TERMINAL.includes(task.status) &&
		task.due.slice(0, 10) < new Date(now).toISOString().slice(0, 10);
	const items = [
		...(task.assignee_id === null ? [] : [assigned]),
		...task.tags.map((tag) =>
			h.span([h.DataAttribute("testid", "task-tag")], [`#${tag}`]),
		),
		...(task.due === null
			? []
			: [
					h.span(
						[
							h.Class(
								`inline-flex items-center gap-1 ${overdue ? "text-error font-medium" : ""}`,
							),
							h.DataAttribute("testid", "task-due"),
						],
						[
							Icons.Clock(h, { size: 12 }),
							i18next.t(overdue ? "tasks.list.overdue" : "tasks.list.due", {
								date: dueText(task.due),
							}),
						],
					),
				]),
		...(task.people.length === 0
			? []
			: [
					h.span(
						[h.Class("inline-flex items-center gap-1")],
						[
							Icons.User(h, { size: 12 }),
							task.people.length === 1
								? i18next.t("tasks.list.onePerson")
								: i18next.t("tasks.list.people", { count: task.people.length }),
						],
					),
				]),
	];
	const desc =
		task.description === null || task.description === ""
			? []
			: [h.span([h.Class("min-w-0 truncate")], [task.description])];
	return items.length + desc.length === 0
		? h.empty
		: h.div(
				[
					h.Class(
						"mt-0.5 flex items-center gap-x-3 max-sm:flex-wrap text-xs text-base-content/60 [&>*]:shrink-0 [&>.truncate]:shrink",
					),
				],
				[...items, ...desc],
			);
};

const marker = (h: H, letter: string, label: string, tone: string): Html =>
	h.span(
		[
			h.Role("img"),
			h.AriaLabel(label),
			h.Title(label),
			h.Class(`badge badge-xs badge-soft ${tone} font-mono font-semibold`),
		],
		[letter],
	);

const markers = (h: H, task: Task): Html =>
	h.div(
		[h.Class("flex gap-1")],
		[
			(task.sections.hypothesis?.length ?? 0) > 0
				? marker(h, "H", i18next.t("tasks.list.hasHypothesis"), "badge-primary")
				: h.empty,
			(task.sections.evidence?.length ?? 0) > 0
				? marker(h, "E", i18next.t("tasks.list.hasEvidence"), "badge-accent")
				: h.empty,
		],
	);

/** A chip with a transparent native select over it: the chip is what you see, the select is what you use. */
const inlineSelect = <A extends string>(
	h: H,
	shown: Html,
	editable: boolean,
	config: Readonly<{
		label: string;
		value: A;
		options: ReadonlyArray<A>;
		optionLabel: (a: A) => string;
		onChange: (value: A) => Message;
	}>,
): Html =>
	!editable
		? shown
		: h.div(
				[h.Class("relative inline-flex rounded hover:bg-base-200")],
				[
					shown,
					h.select(
						[
							h.Class("absolute inset-0 cursor-pointer opacity-0"),
							h.AriaLabel(config.label),
							h.OnChange((value) =>
								config.onChange(
									config.options.find((o) => o === value) ?? config.value,
								),
							),
						],
						config.options.map((option) =>
							h.option(
								[h.Value(option), h.Selected(option === config.value)],
								[config.optionLabel(option)],
							),
						),
					),
				],
			);

const row = (
	h: H,
	shared: Shared,
	model: Model,
	keys: ReadonlyArray<ApiKey>,
	now: number,
	{ task, depth, childCount }: Row,
): Html => {
	const editable = Option.isNone(shared.asOf);
	const isCollapsed = model.collapsed.includes(task.id);
	const closed = TERMINAL.includes(task.status);
	const urgent = task.priority === "urgent" && !closed;
	return h.tr(
		[
			h.Key(task.id),
			h.DataAttribute("testid", "task-row"),
			// Below sm a row is a grid (status over priority, title, grade) so the title keeps its width.
			h.Class(
				`max-sm:grid max-sm:grid-cols-[7.5rem_minmax(0,1fr)_4rem] max-sm:grid-rows-[auto_1fr] max-sm:border-l-4 ${urgent ? "max-sm:border-l-error" : "max-sm:border-l-transparent"} ${closed ? "text-base-content/60" : ""}`,
			),
		],
		[
			h.td(
				[
					h.Class(
						// Open urgent work gets an edge marker so it stands out on a scan.
						`max-sm:col-start-1 max-sm:row-start-1 max-sm:border-l-0 max-sm:pb-0 border-l-4 ${urgent ? "border-l-error" : "border-l-transparent"}`,
					),
				],
				[
					inlineSelect(h, statusChip(h, task.status), editable, {
						label: i18next.t("tasks.list.changeStatus", { title: task.title }),
						value: task.status,
						options: TaskStatus.literals,
						optionLabel: statusLabel,
						onChange: (status) =>
							Message.ChangedStatus({ id: task.id, status }),
					}),
				],
			),
			h.td(
				[
					h.Class(
						"whitespace-nowrap max-sm:col-start-1 max-sm:row-start-2 max-sm:pt-0",
					),
				],
				[
					inlineSelect(h, priorityGlyph(h, task.priority), editable, {
						label: i18next.t("tasks.list.changePriority", {
							title: task.title,
						}),
						value: task.priority,
						options: TaskPriority.literals,
						optionLabel: priorityLabel,
						onChange: (priority) =>
							Message.ChangedPriority({ id: task.id, priority }),
					}),
				],
			),
			h.td(
				[
					h.Class(
						"min-w-0 w-full max-sm:col-start-2 max-sm:row-span-2 max-sm:row-start-1",
					),
				],
				[
					h.div(
						[h.Class("flex w-0 min-w-full items-start gap-1")],
						[
							// Subtasks keep the title column; a rail glyph marks them instead of an indent.
							childCount === 0
								? h.span(
										[
											h.AriaHidden(true),
											h.Class("w-5 shrink-0 text-center text-base-content/30"),
										],
										[depth > 0 ? "└" : ""],
									)
								: h.button(
										[
											h.Type("button"),
											h.Class(
												"btn btn-ghost btn-xs btn-square -ml-1 h-5 min-h-5 w-5 shrink-0",
											),
											h.AriaExpanded(!isCollapsed),
											h.AriaLabel(
												i18next.t(
													isCollapsed
														? "tasks.list.expand"
														: "tasks.list.collapse",
													{ title: task.title, count: childCount },
												),
											),
											h.OnClick(Message.ToggledCollapse({ id: task.id })),
										],
										[
											(isCollapsed ? Icons.ChevronRight : Icons.ChevronDown)(
												h,
												{ size: 14 },
											),
										],
									),
							h.div(
								[h.Class("min-w-0")],
								[
									h.a(
										[
											h.Href(
												linkHref(
													shared.route,
													Route.Task({ id: task.id, asOf: shared.asOf }),
												),
											),
											h.Class(
												`link link-hover font-medium ${task.status === "canceled" ? "line-through" : ""}`,
											),
										],
										[task.title],
									),
									isCollapsed
										? h.span(
												[h.Class("ml-2 text-xs text-base-content/50")],
												[
													childCount === 1
														? i18next.t("tasks.list.oneHiddenSubtask")
														: i18next.t("tasks.list.hiddenSubtasks", {
																count: childCount,
															}),
												],
											)
										: h.empty,
									meta(
										h,
										task,
										now,
										assignee(h, shared, keys, task.assignee_id),
									),
								],
							),
						],
					),
				],
			),
			h.td([h.Class("hidden sm:table-cell")], [markers(h, task)]),
			h.td(
				[
					h.Class(
						"max-sm:col-start-3 max-sm:row-span-2 max-sm:row-start-1 max-sm:text-right",
					),
				],
				[
					task.grade === null
						? h.span(
								[
									h.Class("text-base-content/40"),
									h.Role("img"),
									h.AriaLabel(i18next.t("a2aUi.grade.ungraded")),
									h.DataAttribute("testid", "grade-ungraded"),
								],
								["—"],
							)
						: gradeChip(h, task.grade),
				],
			),
			h.td(
				[
					h.Class(
						"hidden whitespace-nowrap text-sm tabular-nums text-base-content/60 sm:table-cell",
					),
				],
				[
					h.time(
						[
							h.Attribute("datetime", task.updated_at),
							h.Title(task.updated_at),
						],
						[relativeTime(task.updated_at, now)],
					),
				],
			),
		],
	);
};

/** A menu item that toggles one filter value; aria-pressed carries the state. */
const toggleItem = (
	h: H,
	menuId: string,
	pressed: boolean,
	text: string,
	onClick: Message,
	label?: string,
): Html =>
	h.li(
		[],
		[
			h.button(
				[
					h.Type("button"),
					h.Class(pressed ? "menu-active" : ""),
					// A pick closes the menu so it never sits over the rows.
					h.Popovertarget(menuId),
					h.Popovertargetaction("hide"),
					h.AriaPressed(String(pressed)),
					...(label === undefined ? [] : [h.AriaLabel(label)]),
					h.OnClick(onClick),
				],
				[
					h.span(
						[h.Class("w-4")],
						[pressed ? Icons.Check(h, { size: 14 }) : h.empty],
					),
					text,
				],
			),
		],
	);

/**
 * One filter dimension as a popover dropdown: a button that shows how many
 * values are on. The native popover API light-dismisses it on an outside
 * click or Escape; CSS anchor positioning puts it under its button.
 */
const filterMenu = (
	h: H,
	id: string,
	title: string,
	active: number,
	items: (menuId: string) => ReadonlyArray<Html>,
): Html => {
	const menuId = `tasks-filter-${id}`;
	const anchor = `--${menuId}`;
	return h.div(
		[],
		[
			h.button(
				[
					h.Type("button"),
					h.Popovertarget(menuId),
					h.AriaHasPopup("true"),
					h.Attribute("style", `anchor-name:${anchor}`),
					h.Class(
						`btn btn-sm font-normal ${active > 0 ? "btn-primary btn-soft" : "btn-outline border-base-300"}`,
					),
				],
				[
					title,
					active > 0
						? h.span(
								[h.Class("badge badge-xs badge-primary")],
								[String(active)],
							)
						: h.empty,
					Icons.ChevronDown(h, { size: 14, class: "opacity-60" }),
				],
			),
			h.ul(
				[
					h.Id(menuId),
					h.Popover("auto"),
					h.Attribute("style", `position-anchor:${anchor}`),
					h.Class(
						"dropdown menu mt-1 w-48 rounded-box border border-base-300 bg-base-100 p-1 shadow",
					),
				],
				items(menuId),
			),
		],
	);
};

const sortLabel = (sort: TaskSort): string =>
	sort === "attention"
		? i18next.t("tasks.list.sort.attention")
		: sort === "updated_at"
			? i18next.t("tasks.list.sort.updated")
			: sort === "priority"
				? i18next.t("tasks.list.sort.priority")
				: sort === "grade"
					? i18next.t("tasks.list.sort.grade")
					: i18next.t("tasks.list.sort.due");

const gradeFilterLabel = (g: GradeFilter): string =>
	i18next.t(`tasks.list.gradeFilter.${g}`);

const filterBar = (
	h: H,
	model: Model,
	tagOptions: ReadonlyArray<string>,
): Html => {
	const f = model.filters;
	const any =
		f.query !== "" ||
		f.graded !== "all" ||
		f.statuses.length + f.priorities.length + f.tags.length > 0;
	return h.div(
		[
			h.Role("group"),
			h.AriaLabel(i18next.t("tasks.list.filterStatus")),
			h.Class("flex flex-wrap items-center gap-2"),
		],
		[
			h.form(
				[h.Role("search"), h.OnSubmit(Message.SubmittedSearch())],
				[
					h.label(
						[h.Class("input input-sm w-64")],
						[
							Icons.Search(h, { size: 14, class: "opacity-60" }),
							h.input([
								h.Type("search"),
								h.AriaLabel(i18next.t("tasks.list.searchLabel")),
								h.Placeholder(i18next.t("tasks.list.searchPlaceholder")),
								h.Value(model.draftQuery),
								h.OnInput((value) => Message.ChangedQuery({ value })),
							]),
						],
					),
				],
			),
			filterMenu(
				h,
				"status",
				i18next.t("tasks.list.col.status"),
				f.statuses.length,
				(menuId) =>
					TaskStatus.literals.map((status) =>
						toggleItem(
							h,
							menuId,
							f.statuses.includes(status),
							statusLabel(status),
							Message.ToggledStatus({ status }),
						),
					),
			),
			filterMenu(
				h,
				"priority",
				i18next.t("tasks.list.col.priority"),
				f.priorities.length,
				(menuId) =>
					TaskPriority.literals.map((priority) =>
						toggleItem(
							h,
							menuId,
							f.priorities.includes(priority),
							priorityLabel(priority),
							Message.ToggledPriority({ priority }),
						),
					),
			),
			tagOptions.length === 0
				? h.empty
				: filterMenu(
						h,
						"tags",
						i18next.t("tasks.list.tags"),
						f.tags.length,
						(menuId) =>
							tagOptions.map((tag) =>
								toggleItem(
									h,
									menuId,
									f.tags.includes(tag),
									`#${tag}`,
									Message.ToggledTag({ tag }),
									i18next.t("tasks.list.filterTag", { tag }),
								),
							),
					),
			h.div(
				[
					h.Role("group"),
					h.AriaLabel(i18next.t("tasks.list.gradeFilter.label")),
					h.Class("join"),
				],
				GradeFilter.literals.map((g) =>
					h.button(
						[
							h.Type("button"),
							h.Class(
								`btn btn-sm join-item font-normal ${f.graded === g ? "btn-neutral" : "btn-outline border-base-300"}`,
							),
							h.AriaPressed(String(f.graded === g)),
							h.OnClick(Message.ChangedGradeFilter({ graded: g })),
						],
						[gradeFilterLabel(g)],
					),
				),
			),
			any
				? h.button(
						[
							h.Type("button"),
							h.Class("btn btn-ghost btn-sm"),
							h.OnClick(Message.ClearedFilters()),
						],
						[Icons.X(h, { size: 14 }), i18next.t("tasks.list.clearFilters")],
					)
				: h.empty,
			h.div([h.Class("grow")], []),
			h.label(
				[h.Class("flex items-center gap-2 text-sm text-base-content/70")],
				[
					i18next.t("tasks.list.sortBy"),
					h.select(
						[
							h.Class("select select-sm w-40"),
							h.AriaLabel(i18next.t("tasks.list.sortBy")),
							h.OnChange((value) =>
								Message.ChangedSort({
									sort:
										TaskSort.literals.find((s) => s === value) ?? "attention",
								}),
							),
						],
						TaskSort.literals.map((sort) =>
							h.option(
								[h.Value(sort), h.Selected(sort === f.sort)],
								[sortLabel(sort)],
							),
						),
					),
				],
			),
		],
	);
};

const stat = (h: H, label: string, value: string, tone = "", note = ""): Html =>
	h.div(
		[h.Class("flex flex-col gap-0.5 px-4 py-2.5")],
		[
			h.span(
				[
					h.Class(
						"text-xs font-medium uppercase tracking-wide text-base-content/60",
					),
				],
				[label],
			),
			h.span([h.Class(`text-xl font-semibold tabular-nums ${tone}`)], [value]),
			note === ""
				? h.empty
				: h.span([h.Class("text-xs text-base-content/60")], [note]),
		],
	);

/** The server's circle-wide totals; an older server sends none, so count the loaded rows. */
const totalsOf = (data: Loaded): TaskTotals => {
	if (data.totals !== null) return data.totals;
	const grades = data.tasks.flatMap((t) => (t.grade === null ? [] : [t.grade]));
	return {
		open: data.tasks.filter((t) => !TERMINAL.includes(t.status)).length,
		doneSince: null,
		byStatus: Object.fromEntries(
			TaskStatus.literals.map((s) => [
				s,
				data.tasks.filter((t) => t.status === s).length,
			]),
		),
		graded: grades.length,
		avgGrade:
			grades.length === 0
				? null
				: grades.reduce((a, b) => a + b, 0) / grades.length,
	};
};

const statStrip = (h: H, model: Model, totals: TaskTotals): Html => {
	const count = (s: Task["status"]) => String(totals.byStatus[s] ?? 0);
	const avg = totals.avgGrade === null ? "—" : totals.avgGrade.toFixed(1);
	const all = Object.values(totals.byStatus).reduce((a, b) => a + b, 0);
	const ungraded = all - totals.graded;
	const showingUngraded = model.filters.graded === "ungraded";
	return h.div(
		[
			h.DataAttribute("testid", "task-stats"),
			h.Class(
				"grid grid-cols-2 divide-base-300 rounded-box bg-base-200/50 sm:grid-cols-5 sm:divide-x",
			),
		],
		[
			stat(
				h,
				i18next.t("tasks.list.stat.open"),
				String(totals.open),
				"",
				i18next.t("tasks.list.stat.openNote", {
					todo: count("todo"),
					backlog: count("backlog"),
				}),
			),
			stat(h, statusLabel("in_progress"), count("in_progress"), "text-info"),
			totals.doneSince === null
				? stat(h, statusLabel("done"), count("done"), "text-success")
				: stat(
						h,
						i18next.t("tasks.list.stat.doneWeek"),
						String(totals.doneSince),
						"text-success",
						i18next.t("tasks.list.stat.doneAll", { count: count("done") }),
					),
			h.button(
				[
					h.Type("button"),
					h.DataAttribute("testid", "stat-ungraded"),
					h.Class(
						`flex flex-col gap-0.5 px-4 py-2.5 text-left hover:bg-base-200 ${showingUngraded ? "bg-base-200" : ""}`,
					),
					h.AriaPressed(String(showingUngraded)),
					h.AriaLabel(
						i18next.t("tasks.list.stat.showUngraded", { count: ungraded }),
					),
					h.OnClick(
						Message.ChangedGradeFilter({
							graded: showingUngraded ? "all" : "ungraded",
						}),
					),
				],
				[
					h.span(
						[
							h.Class(
								"text-xs font-medium uppercase tracking-wide text-base-content/60",
							),
						],
						[i18next.t("tasks.list.stat.ungraded")],
					),
					h.span(
						[h.Class("text-xl font-semibold tabular-nums text-warning")],
						[
							String(ungraded),
							h.span(
								[h.Class("text-sm font-normal text-base-content/50")],
								[` / ${all}`],
							),
						],
					),
					h.span(
						[h.Class("text-xs text-base-content/60 underline")],
						[
							i18next.t(
								showingUngraded
									? "tasks.list.stat.showAll"
									: "tasks.list.stat.ungradedNote",
							),
						],
					),
				],
			),
			stat(
				h,
				i18next.t("tasks.list.avgGrade"),
				avg,
				"",
				i18next.t("tasks.list.stat.avgNote", { count: totals.graded }),
			),
		],
	);
};

const header = (
	h: H,
	model: Model,
	shared: Shared,
	data: Loaded | null,
): Html => {
	const totals = data === null ? null : totalsOf(data);
	return h.div(
		[h.Class("flex flex-wrap items-end justify-between gap-4")],
		[
			h.div(
				[],
				[
					h.h1(
						[h.Class("text-2xl font-semibold tracking-tight")],
						[i18next.t("tasks.title")],
					),
					h.p(
						[
							h.DataAttribute("testid", "task-counts"),
							h.Class("mt-0.5 text-sm text-base-content/60"),
						],
						[
							totals === null
								? i18next.t("tasks.list.subtitle")
								: i18next.t("tasks.list.counts", {
										open: totals.open,
										graded: totals.graded,
										total: Object.values(totals.byStatus).reduce(
											(a, b) => a + b,
											0,
										),
									}),
						],
					),
				],
			),
			Option.isSome(shared.asOf)
				? h.empty
				: h.form(
						[h.Class("join"), h.OnSubmit(Message.SubmittedCreate())],
						[
							h.input([
								h.Type("text"),
								h.Class("input input-sm join-item w-72"),
								h.AriaLabel(i18next.t("tasks.list.newTitleLabel")),
								h.Placeholder(i18next.t("tasks.list.newTitlePlaceholder")),
								h.Value(model.newTitle),
								h.OnInput((value) => Message.ChangedNewTitle({ value })),
							]),
							h.button(
								[
									h.Type("submit"),
									h.Class("btn btn-primary btn-sm join-item"),
									h.Disabled(model.creating),
									h.AriaBusy(model.creating),
								],
								[Icons.Plus(h, { size: 14 }), i18next.t("tasks.list.create")],
							),
						],
					),
		],
	);
};

const table = (h: H, model: Model, shared: Shared, data: Loaded): Html => {
	const keys = AsyncData.getOrElse(model.keys, () => ({ data: [] })).data;
	const rows = treeRows(data.tasks, model.collapsed);
	const th = (text: string, className = "") =>
		h.th(
			[
				h.Class(
					`text-xs font-medium uppercase tracking-wide text-base-content/50 ${className}`,
				),
			],
			[text],
		);
	return rows.length === 0
		? h.p(
				[
					h.DataAttribute("testid", "tasks-empty"),
					h.Class("py-10 text-center text-base-content/60"),
				],
				[
					model.filters.query !== "" ||
					model.filters.graded !== "all" ||
					model.filters.statuses.length +
						model.filters.priorities.length +
						model.filters.tags.length >
						0
						? i18next.t("tasks.list.noMatches")
						: i18next.t("tasks.list.empty"),
				],
			)
		: h.div(
				[
					h.Class(
						"overflow-x-auto rounded-box border border-base-300 bg-base-100",
					),
				],
				[
					h.table(
						[h.Class("table table-sm max-sm:block")],
						[
							h.thead(
								[h.Class("max-sm:hidden")],
								[
									h.tr(
										[],
										[
											th(i18next.t("tasks.list.col.status")),
											th(i18next.t("tasks.list.col.priority")),
											th(i18next.t("tasks.list.col.task")),
											th(
												i18next.t("tasks.list.col.markers"),
												"hidden sm:table-cell",
											),
											th(i18next.t("tasks.list.col.grade")),
											th(
												i18next.t("tasks.list.col.updated"),
												"hidden sm:table-cell",
											),
										],
									),
								],
							),
							h.tbody(
								[h.Class("max-sm:block")],
								rows.map((r) => row(h, shared, model, keys, data.now, r)),
							),
						],
					),
				],
			);
};

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const data = AsyncData.getOrElse(model.tasks, () => null);
	const tagOptions = [
		...new Set([
			...(data?.tasks.flatMap((t) => t.tags) ?? []),
			...model.filters.tags,
		]),
	].sort();
	return h.section(
		[
			h.DataAttribute("testid", "page-Tasks"),
			h.Class("mx-auto flex w-full max-w-6xl flex-col gap-4"),
		],
		[
			header(h, model, shared, data),
			Option.isSome(shared.asOf)
				? h.p(
						[
							h.DataAttribute("testid", "tasks-live-notice"),
							h.Class("alert alert-info alert-soft text-sm"),
						],
						[i18next.t("tasks.list.liveNotice")],
					)
				: h.empty,
			Option.match(model.alert, {
				onNone: () => h.empty,
				onSome: (text) =>
					h.div(
						[
							h.Role("alert"),
							h.Class("alert alert-warning alert-soft text-sm"),
						],
						[
							h.span([], [text]),
							h.button(
								[
									h.Type("button"),
									h.Class("btn btn-ghost btn-xs"),
									h.AriaLabel(i18next.t("tasks.list.dismiss")),
									h.OnClick(Message.DismissedAlert()),
								],
								[i18next.t("tasks.list.dismiss")],
							),
						],
					),
			}),
			data === null ? h.empty : statStrip(h, model, totalsOf(data)),
			filterBar(h, model, tagOptions),
			data === null
				? AsyncData.isFailure(model.tasks)
					? h.div(
							[h.Role("alert"), h.Class("alert alert-error")],
							[h.span([], [i18next.t("tasks.list.loadError")])],
						)
					: h.div(
							[
								h.Role("status"),
								h.Class("flex items-center gap-2 py-8 text-base-content/70"),
							],
							[
								Icons.Loader2(h, { size: 20, class: "animate-spin" }),
								h.span([], [i18next.t("tasks.list.loading")]),
							],
						)
				: table(h, model, shared, data),
			data?.cursor == null
				? h.empty
				: h.div(
						[h.Class("flex justify-center")],
						[
							h.button(
								[
									h.Type("button"),
									h.Class("btn btn-sm"),
									h.Disabled(model.loadingMore),
									h.AriaBusy(model.loadingMore),
									h.OnClick(Message.ClickedLoadMore()),
								],
								[
									i18next.t(
										model.loadingMore
											? "tasks.list.loadingMore"
											: "tasks.list.loadMore",
									),
								],
							),
						],
					),
		],
	);
});
